import React from 'react';

import { useNode, useEditor } from '@craftjs/core';
import { useTranslation } from 'react-i18next';

import { Badge, Button, ButtonGroup, Tooltip } from '@mui/material';
import TableChartIcon from '@mui/icons-material/TableChart';
import GridOnIcon from '@mui/icons-material/GridOn';
import AddBoxIcon from '@mui/icons-material/AddBox';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import TableRowsIcon from '@mui/icons-material/TableRows';

import { getListOfNodes, filterNodesByParentId } from '@common/helpers/helpers';
import { TABLE_SEGMENT_OPTIONS } from '@common/constants';

const TableSegments = ({
  options = [],
  onAddColumn = () => {},
  onRemoveColumn = () => {},
  onAddRow = () => {},
  onRemoveRow = () => {},
  isFixed = false,
}) => {
  const { t } = useTranslation();

  const { query, actions } = useEditor();
  const {
    actions: { setProp },
    showBorder,
    showHeader,
    showFooter,
    id,
  } = useNode(({ data: { props } }) => {
    return {
      showBorder: props.showBorder,
      showHeader: props.showHeader,
      showFooter: props.showFooter,
    };
  });

  const fieldValue = {
    showBorder,
    showHeader,
    showFooter,
    addColumn: true,
    removeColumn: true,
    addRow: isFixed,
    removeRow: isFixed,
  };

  const onClick = (event) => {
    const fieldName = event?.currentTarget?.name;

    if (fieldName === TABLE_SEGMENT_OPTIONS.SHOW_BORDER.name) {
      const allNodes = getListOfNodes(query);
      const tableRows = allNodes.filter(filterNodesByParentId(id));
      const tableCells = allNodes.reduce((acc, tableCell) => {
        tableRows.forEach((rowNode) => {
          if (rowNode.data.nodes.includes(tableCell.id)) {
            acc.push(tableCell);
          }
        });
        return acc;
      }, []);

      const batched = actions.history.merge();

      tableCells.forEach((child) => {
        batched.setProp(child.id, (props) => {
          props.borderAllShow = !props.borderAllShow;
        });
      });

      setProp((props) => {
        props[fieldName] = !Boolean(fieldValue[fieldName]);
      }, 500);
    } else if (fieldName === TABLE_SEGMENT_OPTIONS.ADD_COLUMN.name) {
      onAddColumn();
    } else if (fieldName === TABLE_SEGMENT_OPTIONS.REMOVE_COLUMN.name) {
      onRemoveColumn();
    } else if (fieldName === TABLE_SEGMENT_OPTIONS.ADD_ROW.name) {
      onAddRow();
    } else if (fieldName === TABLE_SEGMENT_OPTIONS.REMOVE_ROW.name) {
      onRemoveRow();
    } else {
      setProp((props) => {
        props[fieldName] = !Boolean(fieldValue[fieldName]);
      }, 500);
    }
  };

  const renderIcon = (iconName) => {
    switch (iconName) {
      case TABLE_SEGMENT_OPTIONS.SHOW_HEADER.name:
        return <TableChartIcon sx={{ fontSize: '24px !important' }} />;
      case TABLE_SEGMENT_OPTIONS.SHOW_FOOTER.name:
        return (
          <TableChartIcon
            sx={{ transform: 'rotate(180deg)', fontSize: '24px !important' }}
          />
        );
      case TABLE_SEGMENT_OPTIONS.SHOW_BORDER.name:
        return <GridOnIcon sx={{ fontSize: '24px !important' }} />;
      case TABLE_SEGMENT_OPTIONS.ADD_COLUMN.name:
        return (
          <Badge
            overlap='circular'
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={<AddBoxIcon sx={{ fontSize: 14, ml: '4px' }} />}
          >
            <TableRowsIcon sx={{ transform: 'rotate(90deg)' }} />
          </Badge>
        );
      case TABLE_SEGMENT_OPTIONS.REMOVE_COLUMN.name:
        return (
          <Badge
            overlap='circular'
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={<RemoveCircleIcon sx={{ fontSize: 14, ml: '4px' }} />}
          >
            <TableRowsIcon sx={{ transform: 'rotate(90deg)' }} />
          </Badge>
        );
      case TABLE_SEGMENT_OPTIONS.ADD_ROW.name:
        return (
          <Badge
            overlap='circular'
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={<AddBoxIcon sx={{ fontSize: 14, ml: '4px' }} />}
          >
            <TableRowsIcon />
          </Badge>
        );
      case TABLE_SEGMENT_OPTIONS.REMOVE_ROW.name:
        return (
          <Badge
            overlap='circular'
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={<RemoveCircleIcon sx={{ fontSize: 14, ml: '4px' }} />}
          >
            <TableRowsIcon />
          </Badge>
        );
      default:
        return '';
    }
  };

  return (
    <div style={{ display: 'block' }}>
      <ButtonGroup
        disableElevation
        sx={{ border: 'none' }}
        aria-label='Basic button group'
        size='small'
      >
        {options.map((option) => (
          <Tooltip key={option.name} title={t(option.label)}>
            <span>
              <Button
                name={option.name}
                onClick={onClick}
                sx={(theme) => ({
                  p: 1,
                  '& span': {
                    m: 0,
                  },
                  backgroundColor: theme.palette.secondary.light,
                  color: fieldValue[option.name]
                    ? theme.palette.secondary.dark
                    : theme.common.buttonInactive,
                  border: `none`,
                })}
                startIcon={renderIcon(option.iconName)}
                disabled={
                  !isFixed && ['addRow', 'removeRow'].includes(option.name)
                }
              />
            </span>
          </Tooltip>
        ))}
      </ButtonGroup>
    </div>
  );
};

export default TableSegments;
